import React, { useEffect, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useEditorStore } from '../../store/editorStore';
import OverlayStage from '../overlay/OverlayStage';

const PAGE_GAP = 16; // Gap between pages in pixels

// Global render queue to prevent concurrent canvas renders
const renderQueue = {
  current: Promise.resolve(),
};

const PdfViewport = ({ documentModel }) => {
  const containerRef = useRef(null);
  const { scale, setCurrentPage } = useEditorStore();
  const [renderedPages, setRenderedPages] = useState(new Set());

  if (!documentModel) return null;

  const { pdfDocument, numPages, pages } = documentModel;

  // Calculate row heights (each page height + gap)
  const rowVirtualizer = useVirtualizer({
    count: numPages,
    getScrollElement: () => containerRef.current,
    estimateSize: (index) => {
      const page = pages[index];
      return (page ? page.height * scale : 800) + PAGE_GAP;
    },
    overscan: 2, // Render 2 pages above/below viewport
  });

  // Track visible page for current page indicator
  useEffect(() => {
    const visibleItems = rowVirtualizer.getVirtualItems();
    if (visibleItems.length > 0) {
      // Set current page to the first visible page
      setCurrentPage(visibleItems[0].index + 1);
    }
  }, [rowVirtualizer.getVirtualItems(), setCurrentPage]);

  return (
    <div
      ref={containerRef}
      className="pdf-viewport overflow-auto bg-gray-100"
      style={{
        height: 'calc(100vh - 140px)', // Dynamic height based on viewport
        width: '100%'
      }}
    >
      {/* Virtual list container */}
      <div
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {/* Render visible pages */}
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const pageNum = virtualRow.index + 1; // 1-based index for PDF.js
          const pageMetrics = pages[virtualRow.index];

          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <div className="page-container mx-auto" style={{ width: 'fit-content' }}>
                <PageCanvas
                  pdfDocument={pdfDocument}
                  pageNum={pageNum}
                  pageMetrics={pageMetrics}
                  scale={scale}
                  onRendered={() => {
                    setRenderedPages((prev) => new Set(prev).add(pageNum));
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Individual page canvas component
const PageCanvas = ({ pdfDocument, pageNum, pageMetrics, scale, onRendered }) => {
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const isRenderingRef = useRef(false);
  const mountedRef = useRef(true);
  
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  
  useEffect(() => {
    if (!canvasRef.current || !pdfDocument) return;
    
    const canvas = canvasRef.current;
    let cancelled = false;
    
    const render = async () => {
      // Add to global render queue to prevent concurrent renders
      renderQueue.current = renderQueue.current.then(async () => {
        // Wait if already rendering this specific canvas
        if (isRenderingRef.current) {
          // Cancel previous render
          if (renderTaskRef.current) {
            try {
              renderTaskRef.current.cancel();
            } catch (e) {
              // Ignore cancellation errors
            }
            renderTaskRef.current = null;
          }
          // Wait for cancellation to complete
          await new Promise(resolve => setTimeout(resolve, 50));
        }
        
        if (cancelled || !mountedRef.current) return;
        
        isRenderingRef.current = true;
        
        try {
          const page = await pdfDocument.getPage(pageNum);
          if (cancelled || !mountedRef.current) return;
          
          const viewport = page.getViewport({ scale });
          
          // Get device pixel ratio for crisp rendering
          const dpr = window.devicePixelRatio || 1;
          
          // Set canvas dimensions
          canvas.width = viewport.width * dpr;
          canvas.height = viewport.height * dpr;
          canvas.style.width = `${viewport.width}px`;
          canvas.style.height = `${viewport.height}px`;
          
          const ctx = canvas.getContext('2d', { willReadFrequently: false });
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.save();
          ctx.scale(dpr, dpr);
          
          // Create render task
          const renderContext = {
            canvasContext: ctx,
            viewport: viewport,
          };
          
          renderTaskRef.current = page.render(renderContext);
          await renderTaskRef.current.promise;
          
          ctx.restore();
          
          if (cancelled || !mountedRef.current) return;
          
          renderTaskRef.current = null;
          if (onRendered) onRendered();
        } catch (error) {
          // Silently ignore all cancellation and abort errors
          if (
            error.name === 'RenderingCancelledException' ||
            error.message?.includes('cancelled') ||
            error.message?.includes('abort')
          ) {
            // Expected cancellation, do nothing
            return;
          }
          // Log other errors only once
          if (!cancelled && mountedRef.current) {
            console.error(`Error rendering page ${pageNum}:`, error);
          }
        } finally {
          isRenderingRef.current = false;
        }
      });
    };
    
    // Small delay to ensure previous renders are cleaned up
    const timeoutId = setTimeout(() => {
      if (!cancelled && mountedRef.current) {
        render();
      }
    }, 10);
    
    // Cleanup function
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (e) {
          // Ignore errors during cleanup
        }
        renderTaskRef.current = null;
      }
      
      isRenderingRef.current = false;
    };
  }, [pdfDocument, pageNum, scale]);
  
  return (
    <div className="relative bg-white shadow-lg" style={{ marginBottom: `${PAGE_GAP}px` }}>
      <canvas ref={canvasRef} />
      {/* Overlay for field placement */}
      <OverlayStage pageNum={pageNum} pageMetrics={pageMetrics} scale={scale} />
    </div>
  );
};

export default PdfViewport;
